import type { SubmissionTelemetryEvent, SubmissionTelemetrySink } from "@fabric-harness/sdk";
import type { DatabricksLineageRecord } from "./governance.js";
import type { LakebaseClient } from "./lakebase.js";

/**
 * Cost + lineage as core telemetry (v2, workstream C4): submission lifecycle
 * events and governed tool-call lineage land in Lakebase tables keyed by the
 * same `submissionId` that drives events, settlement, and audit — so spend,
 * lineage, and outcomes join on one stable key. Writes are fire-and-forget:
 * a telemetry failure must never affect execution.
 */
export interface DatabricksTelemetryOptions {
  client: LakebaseClient;
  /** Create the telemetry tables on first use. Default true. */
  initialize?: boolean;
  /** Failure reporter (default console.error). */
  onError?: (context: string, error: unknown) => void;
}

export interface DatabricksTelemetry {
  /** Wire into `createSubmissionRunner({ telemetry })`. */
  sink: SubmissionTelemetrySink;
  /** Wire into `withGovernance({ onLineage })` / the `databricks()` bundle. */
  onLineage: (record: DatabricksLineageRecord) => void;
  /** Idempotent schema setup (also runs lazily on first write). */
  migrate(): Promise<void>;
}

/** Create the telemetry tables when absent. Idempotent. */
export async function ensureDatabricksTelemetryTables(client: LakebaseClient): Promise<void> {
  await client.query(`CREATE TABLE IF NOT EXISTS fh_submission_telemetry (
    id BIGSERIAL PRIMARY KEY,
    submission_id TEXT NOT NULL,
    event_type TEXT NOT NULL,
    kind TEXT NOT NULL,
    agent TEXT NOT NULL,
    instance_id TEXT NOT NULL,
    session TEXT NOT NULL,
    attempt_id TEXT,
    attempt_count INTEGER,
    outcome TEXT,
    error TEXT,
    duration_ms BIGINT,
    model TEXT,
    input_tokens BIGINT,
    output_tokens BIGINT,
    cost_usd DOUBLE PRECISION,
    tenant_id TEXT,
    principal_kind TEXT,
    principal_id TEXT,
    at BIGINT NOT NULL
  )`);
  await ensureIndex(
    client,
    "fh_submission_telemetry",
    "fh_submission_telemetry_submission_idx",
    "CREATE INDEX IF NOT EXISTS fh_submission_telemetry_submission_idx ON fh_submission_telemetry (submission_id)",
  );
  await client.query(`CREATE TABLE IF NOT EXISTS fh_lineage (
    id BIGSERIAL PRIMARY KEY,
    submission_id TEXT,
    attempt_id TEXT,
    tool TEXT NOT NULL,
    service TEXT,
    provider TEXT,
    principal TEXT,
    catalog TEXT,
    schema_name TEXT,
    object_name TEXT,
    operation TEXT,
    ok BOOLEAN NOT NULL,
    error TEXT,
    tenant_id TEXT,
    input JSONB,
    started_at TEXT NOT NULL,
    finished_at TEXT NOT NULL
  )`);
  await ensureColumn(client, "fh_lineage", "object_name", "TEXT");
  await ensureColumn(client, "fh_lineage", "operation", "TEXT");
  await ensureIndex(
    client,
    "fh_lineage",
    "fh_lineage_submission_idx",
    "CREATE INDEX IF NOT EXISTS fh_lineage_submission_idx ON fh_lineage (submission_id)",
  );
}

async function ensureColumn(
  client: LakebaseClient,
  table: string,
  column: string,
  type: string,
): Promise<void> {
  const existing = await client.query(
    `SELECT 1 AS present
       FROM pg_catalog.pg_attribute
      WHERE attrelid = pg_catalog.to_regclass($1)
        AND attname = $2
        AND attnum > 0
        AND NOT attisdropped
      LIMIT 1`,
    [table, column],
  );
  if (existing.rows.length > 0) return;
  await client.query(`ALTER TABLE ${table} ADD COLUMN IF NOT EXISTS ${column} ${type}`);
}

async function ensureIndex(
  client: LakebaseClient,
  table: string,
  index: string,
  createSql: string,
): Promise<void> {
  const existing = await client.query(
    `SELECT 1 AS present
       FROM pg_catalog.pg_class AS index_relation
       JOIN pg_catalog.pg_index AS index_metadata
         ON index_metadata.indexrelid = index_relation.oid
      WHERE index_metadata.indrelid = pg_catalog.to_regclass($1)
        AND index_relation.relname = $2
      LIMIT 1`,
    [table, index],
  );
  if (existing.rows.length > 0) return;
  await client.query(createSql);
}

export function databricksTelemetry(options: DatabricksTelemetryOptions): DatabricksTelemetry {
  const { client } = options;
  const onError =
    options.onError ??
    ((context: string, error: unknown) => {
      console.error(`[fabric-harness:databricks-telemetry] ${context}:`, error);
    });

  let initialized: Promise<void> | undefined;
  const ensure = (): Promise<void> => {
    if (options.initialize === false) return Promise.resolve();
    initialized ??= ensureDatabricksTelemetryTables(client).catch((error) => {
      initialized = undefined;
      throw error;
    });
    return initialized;
  };

  async function writeEvent(event: SubmissionTelemetryEvent): Promise<void> {
    await ensure();
    const attemptId = "attemptId" in event ? (event.attemptId ?? null) : null;
    const attemptCount = "attemptCount" in event ? (event.attemptCount ?? null) : null;
    const outcome = event.type === "submission_settled" ? event.outcome : null;
    const error = event.type === "submission_settled" ? (event.error ?? null) : null;
    const durationMs = event.type === "submission_settled" ? (event.durationMs ?? null) : null;
    const model = event.type === "submission_usage" ? (event.model ?? null) : null;
    const inputTokens = event.type === "submission_usage" ? (event.inputTokens ?? null) : null;
    const outputTokens = event.type === "submission_usage" ? (event.outputTokens ?? null) : null;
    const costUsd = event.type === "submission_usage" ? (event.costUsd ?? null) : null;
    await client.query(
      `INSERT INTO fh_submission_telemetry
        (submission_id, event_type, kind, agent, instance_id, session, attempt_id, attempt_count, outcome, error, duration_ms, model, input_tokens, output_tokens, cost_usd, tenant_id, principal_kind, principal_id, at)
       VALUES ($1, $2, $3, $4, $5, $6, $7, $8, $9, $10, $11, $12, $13, $14, $15, $16, $17, $18, $19)`,
      [
        event.submissionId,
        event.type,
        event.kind,
        event.agent,
        event.instanceId,
        event.session,
        attemptId,
        attemptCount,
        outcome,
        error,
        durationMs,
        model,
        inputTokens,
        outputTokens,
        costUsd,
        event.tenantId ?? null,
        event.actor?.principal?.kind ?? null,
        event.actor?.principal?.id ?? null,
        event.at,
      ],
    );
  }

  async function writeLineage(record: DatabricksLineageRecord): Promise<void> {
    await ensure();
    await client.query(
      `INSERT INTO fh_lineage
        (submission_id, attempt_id, tool, service, provider, principal, catalog, schema_name, object_name, operation, ok, error, tenant_id, input, started_at, finished_at)
       VALUES ($1, $2, $3, $4, $5, $6, $7, $8, $9, $10, $11, $12, $13, $14::jsonb, $15, $16)`,
      [
        record.submissionId ?? null,
        record.attemptId ?? null,
        record.tool,
        record.service ?? null,
        record.provider ?? null,
        record.principal ?? null,
        record.catalog ?? null,
        record.schema ?? null,
        record.object ?? null,
        record.operation ?? null,
        record.ok,
        record.error ?? null,
        record.tenantId ?? null,
        JSON.stringify(record.input ?? {}),
        record.startedAt,
        record.finishedAt,
      ],
    );
  }

  return {
    sink: {
      record(event) {
        return writeEvent(event);
      },
    },
    onLineage(record) {
      void writeLineage(record).catch((error) =>
        onError(`lineage write failed for ${record.tool}`, error),
      );
    },
    migrate: ensure,
  };
}

export interface DatabricksLineageEvidenceRow {
  submissionId: string;
  actor?: string;
  object?: string;
  operation?: string;
  outcome: "succeeded" | "failed";
  estimatedCostUsd: number;
  startedAt: string;
  finishedAt: string;
}

/** Join governed object access to the submission actor, outcome, and model/tool cost. */
export async function queryDatabricksLineageEvidence(
  client: LakebaseClient,
  submissionId: string,
): Promise<DatabricksLineageEvidenceRow[]> {
  const result = await client.query(
    `SELECT l.submission_id, COALESCE(l.principal, MAX(t.principal_id)) AS actor,
            l.object_name, l.operation, l.ok, COALESCE(SUM(t.cost_usd), 0) AS estimated_cost_usd,
            l.started_at, l.finished_at
       FROM fh_lineage l
       LEFT JOIN fh_submission_telemetry t ON t.submission_id = l.submission_id
      WHERE l.submission_id = $1
      GROUP BY l.id, l.submission_id, l.principal, l.object_name, l.operation, l.ok, l.started_at, l.finished_at
      ORDER BY l.id`,
    [submissionId],
  );
  return result.rows.map((row) => ({
    submissionId: String(row.submission_id),
    ...(row.actor ? { actor: String(row.actor) } : {}),
    ...(row.object_name ? { object: String(row.object_name) } : {}),
    ...(row.operation ? { operation: String(row.operation) } : {}),
    outcome: row.ok ? "succeeded" : "failed",
    estimatedCostUsd: Number(row.estimated_cost_usd ?? 0),
    startedAt: String(row.started_at),
    finishedAt: String(row.finished_at),
  }));
}
